import {
    ButtonSecondary,
    Colors,
    PageContainer,
    PageHeader,
    Spinner,
    Tabs,
    Text,
} from '@amelio/ui-library';
import { useMemo } from 'react';

import { ViewedPortalBanner } from './components/ViewedPortalBanner';
import { ViewPortalSelect } from './components/ViewPortalSelect';
import { useCompetencyVisibility } from './hooks/useCompetencyVisibility';
import {
    EMPLOYEE_PORTAL_TABS,
    useEmployeePortalTab,
    type EmployeePortalTab,
} from './hooks/useEmployeePortalTab';
import { useIsSummaryTabOffered } from './hooks/useIsSummaryTabOffered';
import { usePortalAudience } from './hooks/usePortalAudience';
import { EmployeePortalObjectivesTab } from './tabs/Objectives/EmployeePortalObjectivesTab';
import { SummaryTab } from './tabs/Summary';
import { RecognitionsSection } from './tabs/Summary/Recognitions';

import type { ComponentType } from 'react';

import { EmptyState } from '@/components/EmptyState/EmptyState';
import ObjectivePeriodFilter from '@/components/objectives/ObjectivePeriodFilter';
import { useAmelioTranslations } from '@/hooks';

/**
 * The summary tab as the portal composes it. The tab draws what the reader's own performance says
 * about them; the recognitions come from Engagement, so the portal hands the section in rather than
 * letting the tab reach for another module's data itself, and the portal is also where what the
 * company offers is read, so the tab decides nothing about it.
 */
const PortalSummaryTab = () => {
    const { areCompetenciesShown } = useCompetencyVisibility();

    return (
        <SummaryTab
            areCompetenciesShown={areCompetenciesShown === true}
            recognitionsSection={<RecognitionsSection />}
        />
    );
};

/** The screen behind each tab. */
const TAB_CONTENT: Record<EmployeePortalTab, ComponentType> = {
    summary: PortalSummaryTab,
    objectives: EmployeePortalObjectivesTab,
};

/**
 * The control a tab hangs at the right end of the tab row, above its banner: what the whole tab is
 * read through, rather than something buried in one of its sections. A tab without one is simply
 * absent here, and the page never learns what any of them contains.
 */
const TAB_HEADER_CONTROL: Partial<Record<EmployeePortalTab, ComponentType>> = {
    objectives: ObjectivePeriodFilter,
};

/**
 * The employee's own performance portal: a header, then one tab per part of their performance. Open
 * to every employee with access to the module; each tab reads the reader's own data.
 */
export const EmployeePortalPage = () => {
    const { t } = useAmelioTranslations();
    const isSummaryOffered = useIsSummaryTabOffered();
    const { isUnreadable: arePreferencesUnreadable, retry } = useCompetencyVisibility();

    const availableTabs = useMemo(
        () => EMPLOYEE_PORTAL_TABS.filter((tab) => tab !== 'summary' || isSummaryOffered === true),
        [isSummaryOffered],
    );
    const { activeTab, openTab } = useEmployeePortalTab(availableTabs);

    // Until the summary tab has answered for its sections, no tab can be offered or withheld:
    // deciding early would blink a tab in, or send a `?tab=` link to another one for a beat. A
    // company answer that could not be read is an answer all the same -- the summary tab stays
    // undecided and is said to be, while the tabs that never depended on it still open.
    const isPortalKnown = isSummaryOffered !== undefined || arePreferencesUnreadable;

    return (
        <PageContainer dataTestId={'employee-portal-page'}>
            <div className={'flex flex-col max-md:gap-8 md:gap-12'}>
                <PageHeader
                    title={t('employeePortal.header.title')}
                    subtitle={
                        <Text
                            as={'span'}
                            color={Colors.NAVY}
                            className={'text-[13px]'}
                        >
                            {t('employeePortal.header.subtitle')}
                        </Text>
                    }
                    iconName={'business-activity'}
                    size={'md'}
                    className={'max-w-[519px]'}
                    dataTestId={'employee-portal-header'}
                />

                <PortalToolbar
                    availableTabs={isPortalKnown ? availableTabs : []}
                    activeTab={isPortalKnown ? activeTab : undefined}
                    onOpenTab={openTab}
                />

                <ViewedPortalBanner />

                {arePreferencesUnreadable && <PortalReadFailure onRetry={retry} />}

                {!arePreferencesUnreadable && !isPortalKnown && (
                    <div className={'flex justify-center py-10'}>
                        <Spinner
                            size={'md'}
                            color={Colors.PRIMARY}
                            dataTestId={'employee-portal-loading'}
                        />
                    </div>
                )}

                {/* "Nothing enabled" is a statement about the company, so it is only made when the
                    company could be asked: a failed read leaves the failure to speak for itself. */}
                {isPortalKnown && activeTab === undefined && !arePreferencesUnreadable && (
                    <EmptyState
                        title={t('employeePortal.noTabs.title')}
                        message={t('employeePortal.noTabs.description')}
                    />
                )}

                {isPortalKnown && activeTab !== undefined && <PortalPanel tab={activeTab} />}
            </div>
        </PageContainer>
    );
};

/**
 * What the portal draws when the company's own answer could not be read: it says so, and offers to
 * ask again. Neither a wait nor a decision -- the two the reader would otherwise be shown instead.
 */
const PortalReadFailure = ({ onRetry }: { onRetry: () => void }) => {
    const { t } = useAmelioTranslations();

    return (
        <div className={'flex flex-col items-center gap-4 py-10 text-center'}>
            <p
                role={'alert'}
                className={'text-error text-sm'}
            >
                {t('employeePortal.loadError')}
            </p>

            <ButtonSecondary
                size={'md'}
                fitContent
                onClick={onRetry}
                dataTestId={'employee-portal-retry'}
            >
                {t('employeePortal.retry')}
            </ButtonSecondary>
        </div>
    );
};

interface PortalToolbarProps {
    /** Empty while the tabs are being decided: the row still draws, without them. */
    availableTabs: readonly EmployeePortalTab[];
    activeTab: EmployeePortalTab | undefined;
    onOpenTab: (tab: EmployeePortalTab) => void;
}

/**
 * The row above the panel: the tabs, and at its right end what the page is read through -- the open
 * tab's own lens, then the portal being read.
 *
 * The picker belongs to the page rather than to the tabs. Hung on the tabs, it would blink out
 * while they are being decided, and be missing altogether from a company that offers none.
 */
const PortalToolbar = ({ availableTabs, activeTab, onOpenTab }: PortalToolbarProps) => {
    const { t } = useAmelioTranslations();
    const audience = usePortalAudience();
    const ActiveTabHeaderControl = activeTab && TAB_HEADER_CONTROL[activeTab];

    return (
        <div className={'flex flex-wrap items-center justify-between gap-4'}>
            {activeTab !== undefined && (
                /* `pills` is the library's own tile row: it scrolls sideways on its own and carries
                   the arrow-key navigation, so no wrapper here. */
                <Tabs
                    items={availableTabs.map((tab) => ({
                        key: tab,
                        label: t(`employeePortal.tabs.${tab}`),
                        dataTestId: `employee-portal-tab-${tab}`,
                    }))}
                    activeKey={activeTab}
                    onChange={(key) => onOpenTab(key as EmployeePortalTab)}
                    variant={'pills'}
                    ariaLabel={t('employeePortal.tabs.ariaLabel')}
                    className={'w-fit max-w-full'}
                />
            )}

            {/* Pushed to the far end while the tabs leave room, and onto the next line once they
                stop -- the same rule at every width, so there is nothing to keep in step. On a
                phone it takes the row's width: sized by its content instead, a long period name
                would set that width and push the page sideways. */}
            <div className={'flex flex-wrap items-center gap-3 max-sm:w-full'}>
                {ActiveTabHeaderControl && <ActiveTabHeaderControl />}
                {audience.isOffered && <ViewPortalSelect {...audience} />}
            </div>
        </div>
    );
};

/** The screen the open tab opens. */
const PortalPanel = ({ tab }: { tab: EmployeePortalTab }) => {
    const { t } = useAmelioTranslations();
    const TabContent = TAB_CONTENT[tab];

    return (
        <div
            role={'tabpanel'}
            aria-label={t(`employeePortal.tabs.${tab}`)}
            data-testid={`employee-portal-panel-${tab}`}
        >
            <TabContent />
        </div>
    );
};
